import React, { useEffect, useState } from 'react';
import { Button, Input, Select, Snackbar, PopUp } from 'geniebook-ui/dist';
import { SelectOptionType } from 'geniebook-ui/dist/components/Select';
import ImageInput from '@components/exam_result/form/ImageInput';
import {
  useExamTypeList,
  useSubscriptionData,
  useUserDetails,
} from '@hooks/user.hook';
import moment from 'moment';
import * as Yup from 'yup';
import { ValidationError } from 'yup';
import {
  ICheckStudentHasSubmittedExamResult,
  IDeleteExamResult,
  IDeleteExamResultAttachment,
  IExamResultFormData,
  IExistingExamResultData,
} from '@models/interfaces/user.interface';
import { useMutation } from '@tanstack/react-query';
import UserServices from '@services/GeniebookAPI/UserServices.services';
import { useNavigate } from 'react-router-dom';
import { atom, useAtom } from 'jotai';
import { activeSubscriptionSubjectsArrayAtom } from '@atom/examResultAtom';
import { useStudentExamResultDetail } from '@hooks/student.hook';
import { IStudentExamResultImageFile } from '@models/interfaces/student.interface';
import { USER_VN_STUDENT } from '@utils/constants/user.constants';
import Cookies from 'js-cookie';
import Lottie from 'lottie-react';
import LoadingDots from '@assets/animation/loading-dots.json';

interface IFormDataError {
  subject_id?: string;
  subject_name?: string;
  level_id?: string;
  exam_date?: string;
  exam_type_id?: string;
  input_exam_type_others?: string;
  marks?: string;
  max_marks?: string;
  exam_grade_id?: string;
  input_exam_grade_na?: string;
}

interface SubscriptionInterface {
  is_trial_plan?: boolean;
  expiry_date?: string;
}

export const showExamResultSubmissionSuccessAtom = atom('');
interface AddExamResultProps {
  examResultId?: string | null;
}

const AddExamResultForm = ({ examResultId = null }: AddExamResultProps) => {
  const querySubscription = useSubscriptionData();
  const queryExamTypeList = useExamTypeList();
  const examResultDetail = useStudentExamResultDetail(examResultId);
  const userDetailQuery = useUserDetails();
  const navigate = useNavigate();
  const [examTypeSelection, setExamTypeSelection] = useState<
    SelectOptionType[]
  >([]);
  const [isButtonActive, setIsButtonActive] = useState<boolean>(false);
  const [showPopupError, setShowPopupError] = useState<boolean>(false);
  const [errorTitle, setErrorTitle] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [snackbarErrorMessage, setSnackbarErrorMessage] = useState<string>('');
  const [activeSubscriptionSubjects, setActiveSubscriptionSubjects] = useAtom(
    activeSubscriptionSubjectsArrayAtom,
  );
  const [showExitWarning, setShowExitWarning] = useState<boolean>(false);
  const [, setShowSuccessSubmitExamResultSnackbar] = useAtom(
    showExamResultSubmissionSuccessAtom,
  );
  const [availableAcademicDropdownSelect, setAvailableAcademicDropdownSelect] =
    useState<SelectOptionType[]>([]);
  const [availableGradeDropdownSelect, setAvailableGradeDropdownSelect] =
    useState<SelectOptionType[]>([]);
  const [userLevel, setUserLevel] = useState<number>(12); // use 12 years as the default limit

  const availableExamType = [
    '8',
    '9',
    '10',
    '11',
    '12',
    '4',
    '6',
    '5',
    '13',
    '-1',
  ];

  const academicLevel: SelectOptionType[] = [
    { label: 'Primary 1', value: '1' },
    { label: 'Primary 2', value: '2' },
    { label: 'Primary 3', value: '3' },
    { label: 'Primary 4', value: '4' },
    { label: 'Primary 5', value: '5' },
    { label: 'Primary 6', value: '6' },
    { label: 'Secondary 1', value: '7' },
    { label: 'Secondary 2', value: '8' },
    { label: 'Secondary 3', value: '9' },
    { label: 'Secondary 4', value: '10' },
    { label: 'JC1', value: '12' },
    { label: 'JC2', value: '13' },
  ];

  useEffect(() => {
    if (userDetailQuery?.data?.data) {
      const data = userDetailQuery?.data?.data;

      const levelId = data.level_id;

      setAvailableAcademicDropdownSelect(
        academicLevel.slice(0, Number(levelId)),
      );

      setUserLevel(Number(levelId));
    } else {
      setUserLevel(12);
    }
  }, [userDetailQuery?.data?.data]);

  // autohide snackbar after 10 secs
  useEffect(() => {
    if (snackbarErrorMessage) {
      const timer = setTimeout(() => {
        setSnackbarErrorMessage('');
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [snackbarErrorMessage]);

  const [formData, setFormData] = useState<IExamResultFormData>({
    subject_id: undefined,
    exam_date: undefined,
    exam_type_id: undefined,
    level_id: undefined,
    input_exam_type_others: undefined,
    marks: undefined,
    max_marks: undefined,
    exam_grade_id: undefined,
    input_exam_grade_na: undefined,
    image: [],
  });

  const [existingExamResultData, setExistingExamResultData] =
    useState<IExistingExamResultData>({
      student_exam_score_id: 0,
      student_exam_image_ids: [],
    });

  const errorData = [
    { id: '004-10', title: '' },
    { id: '004-11', title: 'Override Entry?' },
  ];

  const [formDataError, setFormDataError] = useState<IFormDataError>({
    subject_id: '',
    exam_date: '',
    level_id: '',
    exam_type_id: '',
    input_exam_type_others: '',
    marks: '',
    max_marks: '',
    exam_grade_id: '',
    input_exam_grade_na: '',
  });

  const [displayedFormData, setDisplayedFormData] =
    useState<IExamResultFormData>({
      subject_id: undefined,
      subject_name: undefined,
      exam_date: undefined,
      exam_type_id: undefined,
      level_id: undefined,
      input_exam_type_others: undefined,
      marks: undefined,
      max_marks: undefined,
      exam_grade_id: undefined,
      input_exam_grade_na: undefined,
      image: [],
    });

  const imageIndex: number[] = Array.from(
    Array((formData.image?.length ?? 0) + 1).keys(),
  );

  const submitExamScore = useMutation(
    (formData: IExamResultFormData) => UserServices.submitExamResult(formData),
    {
      onSuccess: async data => {
        const res = data.data;
        if (res.error) {
          setSnackbarErrorMessage(
            'Unable to upload exam score. Please wait a few minutes, and try again.',
          );

          setIsButtonActive(true);

          return;
        }
        let allImagesUploaded = false;

        if (formData.image) {
          try {
            const uploadPromises = formData.image.map(async item => {
              const imageFormData = new FormData();
              imageFormData.append(
                'student_exam_score_id',
                String(examResultId ?? res.student_exam_score_id),
              );
              imageFormData.append('subject_id', String(formData.subject_id));
              imageFormData.append('image', item);
              imageFormData.append('file_name', item.name);
              await uploadExamResultImages.mutateAsync(imageFormData);
            });
            await Promise.all(uploadPromises);
            allImagesUploaded = true;
          } catch (error) {
            console.error('Error uploading images:', error);
            setSnackbarErrorMessage(
              'Unable to upload exam score images. Please wait a few minutes, and try again.',
            );
          }
        }

        if (allImagesUploaded) {
          setShowSuccessSubmitExamResultSnackbar(
            'Nice job! 20 bubbles will be added to your account once the exam result is verified by our teachers.',
          );
          setTimeout(() => {
            navigate('/exam-result');
          }, 0);
        }
      },
      onError: () => {
        setSnackbarErrorMessage(
          'Unable to upload exam score. Please wait a few minutes, and try again.',
        );

        setIsButtonActive(true);
      },
    },
  );

  const checkIfStudentHasSubmitted = useMutation(
    (formData: ICheckStudentHasSubmittedExamResult) =>
      UserServices.studentHasUploadedExamResult(formData),
    {
      onSuccess(data) {
        const res = data.data;
        if (res.error) {
          const error_code = res.error_code;

          const errorDetail = errorData.find(element => {
            return element.id === error_code;
          });

          if (!errorDetail) {
            console.error('Error detail not found'); // won't be shown to the user but still visible on console log
          } else if (errorDetail.id === '004-10') {
            setSnackbarErrorMessage(res.error_msg);
          } else if (errorDetail.id === '004-11') {
            if (
              examResultId &&
              Number(examResultId) == res.data.student_exam_score_id
            ) {
              deleteExistingExamResultImageDataForUpdate().then(() => {
                handleSave();
              });
            } else {
              if (examResultId) {
                setErrorTitle('Entry already exists!');
                setErrorMessage('Please update the existing exam result data');
              } else {
                setErrorTitle(errorDetail.title);
                setErrorMessage(res.error_msg);
              }

              setExistingExamResultData({
                student_exam_score_id: Number(res.data.student_exam_score_id),
                student_exam_image_ids: res.data.student_exam_image_ids,
              });

              setShowPopupError(true);
            }
          } else {
            setSnackbarErrorMessage('Something wrong happened');
          }
        } else {
          if (examResultId) {
            deleteExistingExamResultImageDataForUpdate().then(() => {
              handleSave();
            });
          } else {
            handleSave();
          }
        }
      },
      onError() {},
    },
  );

  const uploadExamResultImages = useMutation((formData: FormData) => {
    return new Promise((resolve, reject) => {
      UserServices.uploadExamResultImages(formData)
        .then(data => {
          resolve(data);
        })
        .catch(error => {
          reject(error);
        });
    });
  });

  const deleteExamResult = useMutation(
    (formData: IDeleteExamResult) => UserServices.deleteExamResult(formData),
    {
      onSuccess(data) {
        const res = data.data;

        if (res.error) {
          setSnackbarErrorMessage(res.error_msg);
        }
      },
      onError() {},
    },
  );

  const deleteExamResultAttachment = useMutation(
    (formData: IDeleteExamResultAttachment) =>
      UserServices.deleteExamResultAttachment(formData),
    {
      onSuccess(data) {
        const res = data.data;

        if (res.error) {
          setSnackbarErrorMessage(res.error_msg);
        }
      },
      onError() {},
    },
  );

  function isTrial(subscription: SubscriptionInterface) {
    return !!subscription.is_trial_plan;
  }

  function isExpired(subscription: SubscriptionInterface) {
    if (!subscription.expiry_date) {
      return true;
    }

    const now = moment();
    const expiryDate = moment.parseZone(subscription.expiry_date + '+08:00'); // from SGT time

    return expiryDate.isBefore(now);
  }

  useEffect(() => {
    if (activeSubscriptionSubjects.length === 0) {
      if (querySubscription.data?.data?.data) {
        const rawData = querySubscription.data?.data?.data;
        let subscriptionData: SelectOptionType[] = [];

        for (let i = 0; i < rawData.length; i++) {
          const subscription = rawData[i];

          if (!isTrial(subscription) && !isExpired(subscription)) {
            subscriptionData.push({
              value: subscription.subject_id,
              label: subscription.subject,
            });
          }
        }

        setActiveSubscriptionSubjects(subscriptionData);
      }
    }
  }, [activeSubscriptionSubjects, querySubscription.data?.data]);

  useEffect(() => {
    const exam_type_id = examResultDetail.data?.data?.data?.exam_type_id || '';
    const updatedAvailableExamType = exam_type_id
      ? [exam_type_id, ...availableExamType]
      : availableExamType;

    if (queryExamTypeList.data?.data?.data) {
      const rawData = queryExamTypeList.data?.data?.data;
      let examList: SelectOptionType[] = [];

      for (let i = 0; i < rawData.length; i++) {
        const examDetail = rawData[i];

        examList.push({
          value: examDetail.exam_type_id,
          label: examDetail.exam_name,
        });
      }

      const filteredExamList = examList.filter(option =>
        updatedAvailableExamType.includes(option.value.toString()),
      );

      const sortedExamList = filteredExamList.sort((a, b) => {
        return (
          updatedAvailableExamType.indexOf(String(a.value)) -
          updatedAvailableExamType.indexOf(String(b.value))
        );
      });

      setExamTypeSelection(sortedExamList);
    }
  }, [queryExamTypeList.data?.data, examResultDetail.data?.data]);

  useEffect(() => {
    const data = examResultDetail.data?.data;
    if (!data) {
      return;
    }

    const {
      exam_date,
      exam_type_id,
      exam_grade_id,
      other_exam_grade,
      other_exam_type,
      image_files,
      marks = '0',
      max_marks = '0',
      subject_id,
      level_id,
    } = data.data ?? {};

    const selectedSubject = activeSubscriptionSubjects.find(
      subject => subject.value === subject_id,
    );
    const image_ids =
      image_files?.map(
        (image: IStudentExamResultImageFile) => image.exam_score_image_id,
      ) || [];

    const fetchImages = async (image_files: IStudentExamResultImageFile[]) => {
      const newFiles: File[] = [];

      await Promise.all(
        image_files.map(async (image, index) => {
          if (image.image_url) {
            const response = await fetch(image.image_url);
            const blob = await response.blob();
            const extension = image.image_url.split('.').pop();
            const imageName = image.image_url
              .split('/')
              .pop()
              ?.replace(/ /g, '');

            if (imageName) {
              const file = new File([blob], imageName, {
                type: `image/${extension || ''}`,
              });
              newFiles[index] = file;
            }
          }
        }),
      );

      return newFiles;
    };

    setExistingExamResultData({
      student_exam_score_id: Number(examResultId),
      student_exam_image_ids: Array.from(new Set(image_ids ?? [])),
    });
    setFormData(prevState => {
      const newState = {
        ...prevState,
        exam_score_id: examResultId,
        marks: marks !== undefined ? parseInt(marks) : undefined,
        max_marks: max_marks !== undefined ? parseInt(max_marks) : undefined,
        exam_date: moment(exam_date).format('YYYY-MM-DD'),
        exam_type_id: exam_type_id,
        subject_id: subject_id ? parseInt(subject_id) : undefined,
        exam_grade_id: exam_grade_id,
        level_id: level_id ? level_id : undefined,
      };

      if (Array.isArray(image_files)) {
        // Initialize the newFiles array with existing formData.image
        fetchImages(image_files).then(newFiles => {
          setFormDataValue('image', newFiles);
        });
      }

      return newState;
    });

    // subject selection
    setDisplayedFormDataValue('subject_id', subject_id);
    setDisplayedFormDataValue('subject_name', selectedSubject?.label ?? '');

    // grade type selection
    const current_exam_grade_id = exam_grade_id ?? '-1';
    const current_exam_type_id = exam_type_id ?? '-1';

    const selected_level = Number(data.data?.level_id ?? 0);

    if (selected_level >= 1 && selected_level <= 4) {
      setAvailableGradeDropdownSelect(p1p4GradeSelection);
    } else if (selected_level >= 5 && selected_level <= 6) {
      setAvailableGradeDropdownSelect(p5p6GradeSelection);
    } else if (selected_level >= 7 && selected_level <= 10) {
      setAvailableGradeDropdownSelect(s1s4GradeSelection);
    } else {
      setAvailableGradeDropdownSelect(jcGradeSelection);
    }

    setSelectedGrade(current_exam_grade_id);
    if (current_exam_grade_id == '-1') {
      setFormDataValue('input_exam_grade_na', other_exam_grade);
    }

    // exam type selection
    setSelectedExamType(current_exam_type_id);
    if (current_exam_type_id == '-1') {
      setFormDataValue('input_exam_type_others', other_exam_type);
    }
  }, [activeSubscriptionSubjects, examResultDetail.data?.data]);

  // redirect vn students to other page as this feature is not intended for them
  useEffect(() => {
    if (Cookies.get('is_vn_student') === USER_VN_STUDENT) navigate('/profile');
  }, []);

  const [selectedExamType, setSelectedExamType] = useState<string>();

  const p1p4GradeSelection: SelectOptionType[] = [
    {
      value: '18',
      label: 'Band 1',
    },
    {
      value: '19',
      label: 'Band 2',
    },
    {
      value: '20',
      label: 'Band 3',
    },
    {
      value: '21',
      label: 'Band 4',
    },
    {
      value: '-1',
      label: 'Not Applicable',
    },
  ];

  const p5p6GradeSelection: SelectOptionType[] = [
    {
      value: '1',
      label: 'AL1',
    },
    {
      value: '2',
      label: 'AL2',
    },
    {
      value: '3',
      label: 'AL3',
    },
    {
      value: '4',
      label: 'AL4',
    },
    {
      value: '5',
      label: 'AL5',
    },
    {
      value: '6',
      label: 'AL6',
    },
    {
      value: '7',
      label: 'AL7',
    },
    {
      value: '8',
      label: 'AL8',
    },
    {
      value: '-1',
      label: 'Not Applicable',
    },
  ];

  const s1s4GradeSelection: SelectOptionType[] = [
    {
      value: '9',
      label: 'A1',
    },
    {
      value: '10',
      label: 'A2',
    },
    {
      value: '11',
      label: 'B3',
    },
    {
      value: '12',
      label: 'B4',
    },
    {
      value: '13',
      label: 'C5',
    },
    {
      value: '14',
      label: 'C6',
    },
    {
      value: '15',
      label: 'D7',
    },
    {
      value: '16',
      label: 'E8',
    },
    {
      value: '17',
      label: 'F9',
    },
    {
      value: '-1',
      label: 'Not Applicable',
    },
  ];

  const jcGradeSelection: SelectOptionType[] = [
    {
      value: '22',
      label: 'A',
    },
    {
      value: '23',
      label: 'B',
    },
    {
      value: '24',
      label: 'C',
    },
    {
      value: '25',
      label: 'D',
    },
    {
      value: '26',
      label: 'S',
    },
    {
      value: '27',
      label: 'U',
    },
    {
      value: '-1',
      label: 'Not Applicable',
    },
  ];

  const [selectedGrade, setSelectedGrade] = useState<string>();

  function handleExamTypeChange(value: string) {
    setSelectedExamType(value);

    if (value !== '-1') {
      setFormDataValue('input_exam_type_others', undefined);
    }

    setFormDataValue('exam_type_id', value);
    resetErrorMessages();
  }

  function handleGradeChange(value: string) {
    setSelectedGrade(value);

    if (value !== '-1') {
      setFormDataValue('input_exam_grade_na', undefined);
    }

    setFormDataValue('exam_grade_id', value);

    resetErrorMessages();
  }

  function setFormDataValue(key: string, value: unknown) {
    setFormData(formData => ({
      ...formData,
      [key]: value,
    }));

    resetErrorMessages();
  }

  function setDisplayedFormDataValue(key: string, value: unknown) {
    setDisplayedFormData({
      ...displayedFormData,
      [key]: value,
    });
  }

  function resetErrorMessages() {
    setFormDataError({
      subject_id: '',
      exam_date: '',
      exam_type_id: '',
      marks: '',
      max_marks: '',
      exam_grade_id: '',
      level_id: '',
    });

    setIsButtonActive(true);
  }

  const handleInsertImage = async (index: number, file: File) => {
    const newFiles = [...formData.image!];
    newFiles[index] = file;
    setFormDataValue('image', newFiles);
  };

  function handleRemoveImage(index: number) {
    const newFiles = [...formData.image!];
    newFiles.splice(index, 1);
    setFormDataValue('image', newFiles);
  }

  let validationSchema = Yup.object().shape({
    subject_id: Yup.number().required('Select the subject'),
    level_id: Yup.number().required('Select the academic level'),
    exam_date: Yup.date()
      .typeError('')
      .max(new Date(), "Exam date cannot be after today's date")
      .min(
        moment().subtract(Number(userLevel), 'years').startOf('year').toDate(),
        'Invalid date',
      )
      .required('Enter the exam date'),
  });

  const checkSubmission = async () => {
    setIsButtonActive(false);

    if (selectedExamType === '-1') {
      validationSchema = validationSchema.concat(
        Yup.object().shape({
          input_exam_type_others: Yup.string()
            .notOneOf(['-1'], 'Enter the name of the exam')
            .max(30, 'Name cannot exceed 30 characters.')
            .required('Enter the name of the exam'),
        }),
      );
    } else {
      validationSchema = validationSchema.concat(
        Yup.object().shape({
          exam_type_id: Yup.string().required('Select the type of exam'),
        }),
      );
    }

    if (
      formData.marks === undefined ||
      (isNaN(formData.marks) && !(formData.marks == undefined))
    ) {
      if (formData.exam_grade_id === undefined) {
        validationSchema = validationSchema.concat(
          Yup.object().shape({
            max_marks: Yup.number().required('Enter the marks').min(1),
            marks: Yup.number()
              .required('Enter the marks')
              .min(0)
              .max(
                formData.max_marks as number,
                'Exam marks must be less than or equal to total exam marks',
              ),
            exam_grade_id: Yup.string().required('Enter the grade'),
          }),
        );
      } else if (formData.exam_grade_id == '-1') {
        validationSchema = validationSchema.concat(
          Yup.object().shape({
            max_marks: Yup.number().required('Enter the marks').min(1),
            marks: Yup.number()
              .required('Enter the marks')
              .min(0)
              .max(
                formData.max_marks as number,
                'Exam marks must be less than or equal to total exam marks',
              ),
            input_exam_grade_na: Yup.string()
              .max(30, 'Grade cannot exceed 30 characters.')
              .notOneOf(['-1'], 'Enter the grade')
              .required('Enter the grade'),
          }),
        );
      }
    } else {
      validationSchema = validationSchema.concat(
        Yup.object().shape({
          marks: Yup.number()
            .required('Enter the marks')
            .min(0)
            .max(
              formData.max_marks as number,
              'Exam marks must be less than or equal to total exam marks',
            ),
        }),
      );
    }

    try {
      if (!isButtonActive) return;

      await validationSchema.validate(formData, { abortEarly: false });

      await checkIfStudentHasSubmitted.mutateAsync({
        level_id: Number(formData.level_id),
        subject_id: formData.subject_id,
        exam_date: formData.exam_date,
        exam_type_id: formData.exam_type_id,
        exam_score_id: examResultId ?? undefined,
      });
    } catch (validationErrors: unknown) {
      (validationErrors as ValidationError).inner.forEach(message => {
        setFormDataError(previousState => ({
          ...previousState,
          [message.path ?? '']: message.message,
        }));
      });
    }
  };

  const deleteExistingExamResultData = async () => {
    if (existingExamResultData.student_exam_score_id !== undefined) {
      const res = await deleteExamResult.mutateAsync({
        student_exam_score_id: existingExamResultData.student_exam_score_id,
      });

      const body = res.data;

      if (body.error) {
        setSnackbarErrorMessage(body.error_msg);
      } else {
        await deleteAttachment();
      }
    }
  };

  const deleteExistingExamResultImageDataForUpdate = async () => {
    if (existingExamResultData.student_exam_score_id !== undefined) {
      await deleteAttachmentForUpdate();
    }
  };

  const deleteAttachment = async () => {
    if (
      formData.subject_id !== undefined &&
      existingExamResultData.student_exam_image_ids &&
      existingExamResultData.student_exam_image_ids.length > 0
    ) {
      for (const imageId of existingExamResultData.student_exam_image_ids) {
        const res = await deleteExamResultAttachment.mutateAsync({
          exam_image_id: imageId,
          subject_id: formData.subject_id,
        });

        const body = res.data;

        if (body.error) {
          setSnackbarErrorMessage(body.error_msg);

          break;
        }
      }
    }
  };

  const deleteAttachmentForUpdate = async () => {
    if (
      examResultDetail.data?.data.data?.subject_id !== undefined &&
      existingExamResultData.student_exam_image_ids &&
      existingExamResultData.student_exam_image_ids.length > 0
    ) {
      for (const imageId of existingExamResultData.student_exam_image_ids) {
        const res = await deleteExamResultAttachment.mutateAsync({
          exam_image_id: imageId,
          subject_id: Number(examResultDetail.data?.data.data?.subject_id),
        });

        const body = res.data;

        if (body.error) {
          setSnackbarErrorMessage(body.error_msg);

          break;
        }
      }
    }
  };

  const handleSave = async () => {
    const setDefaultMarksValue = (
      value: number | undefined,
      defaultValue: number,
    ) => {
      return value === undefined || isNaN(value) ? defaultValue : value;
    };

    formData.marks = setDefaultMarksValue(formData.marks, 0);
    formData.max_marks = setDefaultMarksValue(formData.max_marks, 0);
    await submitExamScore.mutateAsync(formData);
  };

  function handleCloseForm() {
    navigate('/exam-result');
  }

  return (
    <div className={'w-full pb-6'}>
      {snackbarErrorMessage ? (
        <Snackbar
          message={snackbarErrorMessage}
          buttonText={'Dismiss'}
          onTextClick={() => {
            setSnackbarErrorMessage('');
          }}
          className={'fixed left-1/2 -translate-x-1/2 top-[60px]'}
          states={'error'}
        />
      ) : null}
      {showPopupError && (
        <PopUp
          onClose={() => setShowPopupError(false)}
          buttonGroup={
            <>
              {!examResultId && (
                <Button
                  className="max-w-none"
                  variant="tertiary-raspberry"
                  onClick={() => {
                    setShowPopupError(false);
                    deleteExistingExamResultData().then(() => {
                      handleSave();
                    });
                  }}
                >
                  Proceed
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() => setShowPopupError(false)}
                className="max-w-full"
              >
                Cancel
              </Button>
            </>
          }
          description={errorMessage}
          title={errorTitle}
          disableBodyScroll={true}
          width={'400px'}
        />
      )}
      {showExitWarning && (
        <PopUp
          onClose={() => setShowExitWarning(false)}
          buttonGroup={
            <>
              <Button
                className="max-w-none"
                variant="primary"
                onClick={() => {
                  handleCloseForm();
                }}
              >
                Proceed
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowExitWarning(false)}
                className="max-w-full"
              >
                Cancel
              </Button>
            </>
          }
          description={''}
          title={'Discard changes?'}
          disableBodyScroll={true}
          width={'400px'}
        />
      )}
      <div className={'mb-6'}>
        <Select
          options={activeSubscriptionSubjects}
          value={formData.subject_id}
          className={'[&_input]:py-3 [&_input]:px-4 [&_input]:h-12'}
          placeholder={
            formData.subject_id === undefined
              ? 'Select a subject'
              : displayedFormData.subject_name
          }
          label={'Subject'}
          darkMode={true}
          required={true}
          onChange={value => {
            const subjectIndex = activeSubscriptionSubjects.findIndex(
              item => item.value === value,
            );

            setFormDataValue('subject_id', value);
            setDisplayedFormDataValue(
              'subject_name',
              activeSubscriptionSubjects[subjectIndex].label,
            );
            setDisplayedFormDataValue(
              'subject_id',
              activeSubscriptionSubjects[subjectIndex].value,
            );
          }}
          error={Boolean(formDataError.subject_id !== '')}
          errorMsg={formDataError.subject_id ? formDataError.subject_id : ''}
        />
      </div>
      <div className={'mb-6'}>
        <Select
          options={availableAcademicDropdownSelect}
          value={formData.level_id}
          className={'[&_input]:py-3 [&_input]:px-4 [&_input]:h-12'}
          placeholder={'Select a level'}
          label={'Academic Level'}
          darkMode={true}
          required={true}
          onChange={value => {
            if (value) {
              setFormDataValue('level_id', value);

              value = Number(value);

              if (value >= 1 && value <= 4) {
                setAvailableGradeDropdownSelect(p1p4GradeSelection);
              } else if (value >= 5 && value <= 6) {
                setAvailableGradeDropdownSelect(p5p6GradeSelection);
              } else if (value >= 7 && value <= 10) {
                setAvailableGradeDropdownSelect(s1s4GradeSelection);
              } else {
                setAvailableGradeDropdownSelect(jcGradeSelection);
              }
            }
          }}
          error={Boolean(formDataError.level_id !== '')}
          errorMsg={formDataError.level_id ? formDataError.level_id : ''}
        />
      </div>
      <div className={'mb-6'}>
        <Input
          label={'Exam Date'}
          type={'date'}
          value={formData.exam_date}
          onChange={event => setFormDataValue('exam_date', event.target.value)}
          mode={'dark'}
          placeholder={'Please select date'}
          error={formDataError.exam_date ? formDataError.exam_date : ''}
          min={
            moment()
              .subtract(Number(userLevel), 'years')
              .startOf('year')
              .format('YYYY-MM-DD') as unknown as number
          }
          max={moment().format('YYYY-MM-DD') as unknown as number}
        />
      </div>
      <div className={'mb-6'}>
        <Select
          options={examTypeSelection}
          value={selectedExamType}
          className={'[&_input]:py-3 [&_input]:px-4 [&_input]:h-12'}
          placeholder={'Please select exam type'}
          label={'Exam Type'}
          darkMode={true}
          onChange={value => handleExamTypeChange(value + '')}
          error={
            Boolean(formDataError.exam_type_id !== '') &&
            selectedExamType !== '-1'
          }
          errorMsg={
            formDataError.exam_type_id ? formDataError.exam_type_id : ''
          }
        />
        {selectedExamType === '-1' && (
          <Input
            mode={'dark'}
            type={'text'}
            value={formData.input_exam_type_others}
            onChange={event =>
              setFormDataValue('input_exam_type_others', event.target.value)
            }
            placeholder={'Input your exam type'}
            error={
              formDataError.input_exam_type_others
                ? formDataError.input_exam_type_others
                : ''
            }
          />
        )}
      </div>
      <div className={'mb-6'}>
        <div className={'text-white font-medium leading-4'}>Score</div>
        <div className={'flex gap-2 items-center'}>
          <Input
            type={'number'}
            value={formData.marks}
            onChange={event => {
              const value = event.target.value;
              setFormDataValue(
                'marks',
                value === '' ? undefined : parseInt(value),
              );
            }}
            mode={'dark'}
            min={0}
            placeholder={'100'}
            className={'flex-grow flex-shrink min-w-[80px]'}
            error={formDataError.marks ? formDataError.marks : ''}
            inputProps={{
              onWheel: (event: React.WheelEvent<HTMLInputElement>) =>
                event.currentTarget.blur(),
            }}
          />
          <div
            className={`min-w-[44px] ${
              formDataError.marks
                ? formData.marks !== undefined
                  ? `mt-[-30px]`
                  : `mt-[-15px]`
                : ``
            }`}
          >
            out of
          </div>
          <Input
            type={'number'}
            value={formData.max_marks}
            onChange={event => {
              const value = event.target.value;
              setFormDataValue(
                'max_marks',
                value === '' ? undefined : parseInt(value),
              );
            }}
            mode={'dark'}
            placeholder={'100'}
            min={0}
            className={`flex-grow flex-shrink min-w-[80px] ${
              formDataError.marks && formDataError.max_marks == ''
                ? formData.marks !== undefined
                  ? `mt-[-35px]`
                  : `mt-[-15px]`
                : ``
            }`}
            error={formDataError.max_marks ? formDataError.max_marks : ''}
            inputProps={{
              onWheel: (event: React.WheelEvent<HTMLInputElement>) =>
                event.currentTarget.blur(),
            }}
          />
          <div
            className={`${
              formDataError.marks
                ? formData.marks !== undefined
                  ? `mt-[-30px]`
                  : `mt-[-15px]`
                : ``
            }`}
          >
            marks
          </div>
        </div>
      </div>
      <div className={'mb-6'}>
        <Select
          options={availableGradeDropdownSelect}
          value={selectedGrade}
          className={'[&_input]:py-3 [&_input]:px-4 [&_input]:h-12'}
          placeholder={'Please input your grade'}
          label={'Grade'}
          darkMode={true}
          onChange={value => handleGradeChange(value + '')}
          required={
            formData.marks === undefined && formData.max_marks === undefined
          }
          error={
            Boolean(formDataError.exam_grade_id !== '') &&
            selectedGrade !== '-1'
          }
          errorMsg={
            formDataError.exam_grade_id ? formDataError.exam_grade_id : ''
          }
          disabled={!formData.level_id}
        />
        {selectedGrade === '-1' && (
          <Input
            mode={'dark'}
            type={'text'}
            value={formData.input_exam_grade_na}
            onChange={event =>
              setFormDataValue('input_exam_grade_na', event.target.value)
            }
            placeholder={'Please input your grade'}
            error={
              formDataError.input_exam_grade_na
                ? formDataError.input_exam_grade_na
                : ''
            }
          />
        )}
      </div>
      <div className={'mb-6'}>
        <div className={'mb-4'}>
          <p className={'font-medium text-white'}>Upload exam results</p>
          <p className={'text-primary-navyblue-faded text-sm leading-[19px]'}>
            JPEG and PNG formats, up to 16 MB
          </p>
        </div>
        <div className={'flex gap-2 flex-wrap'}>
          {imageIndex.map(number => {
            return (
              <ImageInput
                key={number}
                onInsert={file => handleInsertImage(number, file)}
                onRemove={() => handleRemoveImage(number)}
                initialFile={formData.image?.[number] ?? false}
              />
            );
          })}
        </div>
      </div>
      <div className={'lg:flex lg:flex-wrap lg:gap-4'}>
        <Button
          className={
            'max-lg:!max-w-full max-lg:!w-full !font-extrabold max-lg:hover:!scale-[1.01] flex items-center'
          }
          onClick={() => {
            setIsButtonActive(false);

            checkSubmission();
          }}
          disabled={!isButtonActive}
        >
          {submitExamScore.isLoading ? (
            <div className="w-8 h-6">
              <Lottie animationData={LoadingDots} className="m-auto" />
            </div>
          ) : (
            'Save'
          )}
        </Button>
        <Button
          variant={'secondary'}
          className={'max-lg:!hidden !font-extrabold'}
          onClick={() => {
            if (isButtonActive) {
              setShowExitWarning(true);
            } else {
              handleCloseForm();
            }
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default AddExamResultForm;
